import { Box, IconButton, Typography } from '@mui/material';
import {
  useExistChannel,
  useChannelMembers,
} from '../hooks/store/useChannelHooks';
import { channelName } from '../channel/ChannelItem';
import BackIcon from '@mui/icons-material/KeyboardArrowLeft';
import { useNavigate } from 'react-router';
import { useCareTeamMembers } from '../hooks/store/useCareTeamMembers';
import { useIntl } from 'react-intl';
import { useCurrentUserGuid } from '../../../firebase/useCollection';
import GalleryIcon from '@mui/icons-material/Collections';
import MemberIcon from '@mui/icons-material/People';
import { useCallback } from 'react';
import { messageNavigationState } from '../../container/MessageContainerNavigationState';
import {
  Routes,
  METRICS_EXPAND_MESSAGING_VIEW,
  METRICS_MESSAGES_MEMBERS_ACCESS,
  useAnalytics,
} from '@xolv-mybrightlink/common';
import { Channel } from '../type';

type MessagingHeaderProps = {
  channelGuid: string;
};

const MessagingHeader = (props: MessagingHeaderProps) => {
  const { channelGuid } = props;
  const intl = useIntl();
  const currentUserGuid = useCurrentUserGuid();
  const channel = useExistChannel(channelGuid);
  const channelMembers = useChannelMembers(channelGuid);
  const careTeamMembers = useCareTeamMembers(channelGuid);
  const showMemberAndGallery = shouldShowMemberAndGallery(channel);
  const navigate = useNavigate();
  const analytics = useAnalytics();

  const openGallery = () => {
    navigate(Routes.messages.channel.gallery(channelGuid));
  };

  const openChannelMember = () => {
    analytics.trackAction(METRICS_MESSAGES_MEMBERS_ACCESS);
    navigate(Routes.messages.channel.members(channelGuid));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'white',
        height: 65,
        borderBottom: 'solid 0.5px #d8dade',
        pl: 10,
        pr: 5,
      }}
    >
      <Typography
        sx={{
          flex: 1,
          fontSize: 16,
          fontWeight: 500,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {channel
          ? channelName(
              currentUserGuid,
              channel,
              channelMembers,
              careTeamMembers,
              intl,
            )
          : ''}
      </Typography>
      {showMemberAndGallery && (
        <IconButton onClick={openGallery} sx={{ color: '#898f9b', mr: 5 }}>
          <GalleryIcon />
        </IconButton>
      )}
      {showMemberAndGallery && (
        <IconButton onClick={openChannelMember} sx={{ color: '#898f9b' }}>
          <MemberIcon />
        </IconButton>
      )}
    </Box>
  );
};

const MessagingHeaderStandalone = (props: {
  channelGuid: string;
  onClose: () => void;
  onOpenGallery: () => void;
  openChannelMember: () => void;
}) => {
  const { channelGuid } = props;
  const intl = useIntl();
  const currentUserGuid = useCurrentUserGuid();
  const channel = useExistChannel(channelGuid);
  const channelMembers = useChannelMembers(channelGuid);
  const careTeamMembers = useCareTeamMembers(channelGuid);
  const shouldShowMemberInHeader =
    channel.type !== 'SCHEDULING_TEAM' && channel.type !== 'PEER_TO_PEER';
  const shouldShowGalleryInHeader =
    channel.type !== 'SCHEDULING_TEAM' && channel.type !== 'PEER_TO_PEER';
  const navigate = useNavigate();
  const analytics = useAnalytics();

  const openMessaging = useCallback(() => {
    analytics.trackAction(METRICS_EXPAND_MESSAGING_VIEW);
    messageNavigationState.collapsedPath = location.pathname;
    navigate(Routes.messages.channel.conversation(channelGuid));
  }, [analytics, navigate, channelGuid]);

  const openChannelMember = useCallback(() => {
    analytics.trackAction(METRICS_MESSAGES_MEMBERS_ACCESS);
    props.openChannelMember();
  }, [analytics, props]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 2,
        borderBottom: 'solid 0.5px #d8dade',
      }}
    >
      <IconButton onClick={props.onClose}>
        <BackIcon color='primary' sx={{ width: 32, height: 32 }} />
      </IconButton>
      <Typography
        sx={{
          flex: 1,
          whiteSpace: 'nowrap',
          fontSize: 16,
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {channel
          ? channelName(
              currentUserGuid,
              channel,
              channelMembers,
              careTeamMembers,
              intl,
            )
          : ''}
      </Typography>
      {shouldShowGalleryInHeader && (
        <IconButton onClick={props.onOpenGallery} sx={{ color: '#898f9b' }}>
          <GalleryIcon />
        </IconButton>
      )}
      {shouldShowMemberInHeader && (
        <IconButton onClick={openChannelMember} sx={{ color: '#898f9b' }}>
          <MemberIcon />
        </IconButton>
      )}
      <IconButton onClick={openMessaging}>
        <img src='/public/assets/icons/ic_expand_care_team.svg' alt='Expand' />
      </IconButton>
    </Box>
  );
};

export { MessagingHeader, MessagingHeaderStandalone };

const shouldShowMemberAndGallery = (channel: Channel) =>
  channel.type !== 'SCHEDULING_TEAM' && channel.type !== 'PEER_TO_PEER';
